import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { SimpleForm } from 'components/forms/SimpleForm';
import { MultilineTextInput } from 'components/inputs/MultilineTextInput';
import { TextInput } from 'components/inputs/TextInput';
import { useGetOrganisations } from 'features/organisations/hooks/useGetOrganisations';
import { LazyAutocompleteInput } from 'greener-ui-library';
import { GreenerEye, Organisation } from 'types';
import { STRING_LENGTHS } from 'utils/constants';
import * as yup from 'yup';

export const GreenerEyeForm = ({
  onSubmit,
  greenerEye,
  isLoading,
  fieldErrors,
  isEditable,
}: {
  onSubmit: () => void;
  greenerEye: GreenerEye;
  isLoading: boolean;
  fieldErrors: Record<string, string>;
  isEditable: boolean;
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const schema = yup.object().shape({
    name: yup.string().required().max(STRING_LENGTHS.MEDIUM),
    externalIdentifier: yup.string().required().max(STRING_LENGTHS.MEDIUM),
    owningOrganisation: yup.object().required(),
    description: yup.string().nullable().max(STRING_LENGTHS.LARGE),
  });
  const [search, setSearch] = useState('');

  const {
    organisations = {},
    isFetching,
    goToNextPage,
    hasNextPage,
  } = useGetOrganisations({
    accumulateData: true,
    searchTerm: search,
    reactQueryConfig: { suspense: false },
  });

  const handleSubmit = (formValues) => {
    onSubmit({
      ...formValues,
      owningOrganisation: formValues.owningOrganisation && {
        ...formValues.owningOrganisation,
        organisationId: formValues.owningOrganisation.organisationId ?? greenerEye?.owningOrganisationId,
      },
    });
  };

  return (
    <SimpleForm
      isEditable={isEditable}
      validationSchema={schema}
      onSubmit={handleSubmit}
      showButtons={isEditable}
      isLoading={isLoading}
      defaultValues={
        greenerEye && {
          name: greenerEye.name,
          externalIdentifier: greenerEye.externalIdentifier,
          owningOrganisation: greenerEye.owningOrganisation
            ? { ...greenerEye.owningOrganisation, organisationId: greenerEye.owningOrganisationId }
            : undefined,
          description: greenerEye.description,
        }
      }
      onGoBack={() => navigate('/greener-eyes')}
      fieldErrors={fieldErrors}
    >
      {({ control, isFormEditable }) => (
        <>
          <TextInput
            control={control}
            name="externalIdentifier"
            label={t('entities:greenerEye.attributes.identifier')}
            readOnly={!isFormEditable}
            gridSize={12}
          />
          <TextInput
            control={control}
            name="name"
            label={t('entities:greenerEye.attributes.name')}
            readOnly={!isFormEditable}
            gridSize={12}
          />
          <LazyAutocompleteInput<Organisation>
            control={control}
            gridSize={12}
            name="owningOrganisation"
            data-testid="owning-org-select"
            label={t('entities:greenerEye.attributes.owningOrganisation')}
            placeholder={t('entities:organisation.selectOrganisation')}
            options={organisations?.accumulatedData}
            loading={isFetching}
            onSearchChange={setSearch}
            onLoadMore={goToNextPage}
            hasNextPage={hasNextPage}
            getOptionLabel={(option) => (typeof option === 'string' ? option : option?.name)}
            isOptionEqualToValue={(option, value) => option.name === value.name}
            disabled={!isFormEditable}
          />
          <MultilineTextInput
            control={control}
            gridSize={12}
            name="description"
            label={t('entities:greenerEye.attributes.description')}
            readOnly={!isFormEditable}
          />
        </>
      )}
    </SimpleForm>
  );
};
